import { z } from "zod"
import { ArrowRight, Lock, Mail } from "lucide-react"
import { Link } from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import { IconInputField } from "@/components/auth/IconInputField"
import { useAuthForm } from "@/lib/use-auth-form"
import { emailSchema, loginPasswordSchema } from "@/lib/auth-schemas"

const schema = z.object({
  email: emailSchema,
  password: loginPasswordSchema,
})

export function LoginForm({ onSubmit, notice }) {
  const { form, serverError, serverErrorCode, clearServerError, submit } = useAuthForm({
    schema,
    defaultValues: { email: "", password: "" },
    onSubmit,
  })
  const confirmationRequired = serverErrorCode === "unconfirmed_account"

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-5" noValidate autoComplete="off">
        <IconInputField
          form={form}
          name="email"
          label="Email Address"
          icon={Mail}
          type="email"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder="operator@sentinel.os"
        />

        <IconInputField
          form={form}
          name="password"
          label="Password"
          icon={Lock}
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          labelAddon={
            <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
              Forgot?
            </Link>
          }
        />

        {notice ? (
          <p className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-medium text-foreground">
            {notice}
          </p>
        ) : null}

        {serverError && !confirmationRequired ? (
          <p
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive"
          >
            {serverError}
          </p>
        ) : null}

        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          className="mt-4 h-12 w-full gap-2 rounded-xl text-sm font-bold shadow-md shadow-primary/20"
        >
          Authenticate &amp; Enter
          <ArrowRight className="h-4 w-4" />
        </Button>
      </form>

      <Dialog open={confirmationRequired} onOpenChange={(open) => !open && clearServerError()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Account confirmation required</DialogTitle>
            <DialogDescription>
              Verify your account confirmation email before signing in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" className="w-full sm:w-auto">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  )
}
